/**
 * Pavage UV des modeles dont la texture couvre tout le carre 0-1.
 *
 * Un design est normalement pose en `uvCenter [0.5,0.5]` / `uvSize [1,1]`, donc
 * etire une seule fois sur toute la texture. Ce fonctionnement convient au
 * t-shirt, dont l'UV est un atlas de panneaux : les SVG ont ete caches sur cet
 * atlas. Il ne convient pas a un modele tubulaire comme le tour de cou, ou la
 * zone imprimable est un unique rectangle deroule :
 *
 *   - le rectangle n'est pas carre (52,2 x 28,8 cm), donc un SVG carre est
 *     etire verticalement du ratio ;
 *   - u=0 et u=1 sont la meme arete physique, donc un motif non periodique
 *     montre une rupture a la couture.
 *
 * On repond aux deux en repetant le design N fois autour de la circonference,
 * avec N pair et une alternance en miroir : chaque tuile est quasi carree (donc
 * non distordue) et tous les raccords, couture incluse, sont continus.
 */

/** Ratio largeur/hauteur physique de la zone imprimable, par fichier de modele. */
export const MODEL_UV_ASPECT: Record<string, number> = {
  // 52.2 cm de tour pour 30.0 cm de hauteur imprimee.
  'TourDeCou.glb': 1.74,
};

/** Modeles dont l'UV se referme sur elle-meme (u=0 et u=1 adjacents). */
export const MODEL_UV_SEAMLESS: Record<string, boolean> = {
  'TourDeCou.glb': true,
};

export type UvTiling = { uvTileCount?: number; uvTileMirror?: boolean };

/**
 * Pavage a appliquer a un design pose en UV globale sur ce modele.
 * Retourne {} pour les modeles classiques, qui conservent le comportement
 * d'origine (un seul etirement plein cadre).
 */
export function getUvTilingForModel(model: { fileName?: string } | null): UvTiling {
  const aspect = model?.fileName ? MODEL_UV_ASPECT[model.fileName] : undefined;
  if (!aspect || aspect <= 1) return {};
  const seamless = Boolean(model?.fileName && MODEL_UV_SEAMLESS[model.fileName]);
  const count = seamless
    ? Math.max(2, Math.round(aspect / 2) * 2)   // pair : la couture reste continue
    : Math.max(1, Math.round(aspect));
  return { uvTileCount: count, uvTileMirror: seamless };
}

/**
 * Distorsion residuelle d'une tuile (1 = tuile parfaitement carree).
 * Utile pour verifier qu'un nombre de repetitions choisi reste acceptable.
 */
export function getTileDistortion(aspect: number, tileCount: number): number {
  if (tileCount <= 0) return 1;
  return aspect / tileCount;
}

/* ── Zone imprimable physique ──────────────────────────────────────────────
   Cotes reelles du produit, en centimetres. Tout le placement (pavage de
   logos, bande reflechissante) s'y refere plutot qu'aux UV, qui sont
   anisotropes sur un modele tubulaire.
   ──────────────────────────────────────────────────────────────────────── */

export type PrintAreaCm = { widthCm: number; heightCm: number };

/** Zone imprimable par fichier de modele. widthCm = tour, heightCm = hauteur. */
export const MODEL_PRINT_AREA_CM: Record<string, PrintAreaCm> = {
  // 2*pi*0.083 m de tour pour 0.30 m de hauteur. La paroi exterieure est
  // desormais imprimee de bord a bord (plus d'ourlet reserve), conformement au
  // gabarit fournisseur qui est un rectangle plein.
  'TourDeCou.glb': { widthCm: 52.2, heightCm: 30.0 },
};

/** Zone imprimable d'un modele, ou null s'il n'en declare pas (t-shirts). */
export function getPrintAreaForModel(model: { fileName?: string } | null): PrintAreaCm | null {
  if (!model?.fileName) return null;
  return MODEL_PRINT_AREA_CM[model.fileName] || null;
}

/* ── Bande reflechissante ─────────────────────────────────────────────────
   Applique verticale pleine hauteur, posee sur une portion du tour. Largeur
   imposee par le procede : c'est une constante, pas un reglage client.
   ──────────────────────────────────────────────────────────────────────── */
export const REFLECTIVE_BAND_WIDTH_CM = 3;
